<template>
  <div class="flex flex-col w-full h-full">
    <!-- Header -->
    <div class="mb-4">
      <h2 class="text-xl font-semibold">
        Manage Accessories
      </h2>
    </div>

    <!-- Search -->
    <div class="mb-4">
      <div class="field">
        <label
          for="search"
          class="block text-sm font-medium mb-1"
        >Search Accessories</label>
        <span class="p-input-icon-left w-full">
          <i class="pi pi-search" />
          <InputText 
            id="search" 
            v-model="searchQuery" 
            placeholder="Search by name, ID, or category..." 
            class="w-full" 
          />
        </span>
      </div>
    </div>

    <!-- Accessories Cards -->
    <div class="flex-1 overflow-hidden">
      <div class="border border-gray-200 rounded-lg shadow-sm h-full flex flex-col">
        <div class="p-3 bg-gray-50 border-b border-gray-200">
          <h3 class="text-lg font-semibold m-0">
            Accessories
          </h3>
        </div>
        <div class="overflow-auto flex-1 p-4">
          <!-- Loading State -->
          <div
            v-if="isLoadingAccessories"
            class="flex justify-center items-center py-12"
          >
            <div class="text-center">
              <i class="pi pi-spin pi-spinner text-gray-400 text-5xl mb-3" />
              <p class="text-gray-600">
                Loading accessories...
              </p>
            </div>
          </div>

          <!-- Error State -->
          <div
            v-else-if="accessoriesError"
            class="flex justify-center items-center py-12"
          >
            <div class="text-center">
              <i class="pi pi-exclamation-triangle text-red-400 text-5xl mb-3" />
              <p class="text-red-600">
                {{ accessoriesError }}
              </p>
            </div>
          </div>
                    
          <!-- Empty State -->
          <div
            v-else-if="filteredAccessories.length === 0"
            class="flex justify-center items-center py-12"
          >
            <div class="text-center">
              <i class="pi pi-search text-gray-400 text-5xl mb-3" />
              <p class="text-gray-600">
                No accessories match your criteria.
              </p>
            </div>
          </div>
                    
          <!-- Accessories Cards -->
          <div
            v-else
            class="space-y-3"
          >
            <div
              v-for="accessory in filteredAccessories"
              :key="accessory.id" 
              class="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <i
                    :class="getAccessoryIcon(accessory.category)"
                    class="text-lg text-gray-600"
                  />
                  <div>
                    <h4 class="font-semibold text-gray-800">
                      {{ accessory.name }}
                    </h4>
                    <p class="text-sm text-gray-600">
                      ID: {{ accessory.id }} • Category: {{ accessory.category }}
                    </p>
                    <p
                      v-if="accessory.notes"
                      class="text-sm text-gray-500 italic"
                    >
                      {{ accessory.notes }}
                    </p>
                    <p class="text-sm text-green-600 font-medium">
                      Total: £{{ calculateTotal(accessory) }}
                    </p>
                  </div>
                </div>
                <div class="flex items-center gap-2">
                  <Badge
                    :value="accessory.accessoryType"
                    :severity="getTypeSeverity(accessory.accessoryType)"
                  />
                  <!-- Delete button for optional accessories -->
                  <Button 
                    v-if="accessory.accessoryType === 'optional'"
                    v-tooltip="'Delete accessory'" 
                    icon="pi pi-trash" 
                    size="small" 
                    text
                    severity="danger"
                    @click="deleteAccessory(accessory)"
                  />
                </div>
              </div>
                            
              <!-- Editable Fields Row -->
              <div class="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                <!-- Type -->
                <div class="field">
                  <label class="block text-xs font-medium text-gray-700 mb-1">Type</label>
                  <Dropdown 
                    v-model="accessory.accessoryType" 
                    :options="accessoryTypeOptions" 
                    option-label="label" 
                    option-value="value"
                    size="small"
                    class="w-full"
                    @change="markAsChanged"
                  />
                </div>
                                
                <!-- Quantity -->
                <div class="field">
                  <label class="block text-xs font-medium text-gray-700 mb-1">Quantity</label>
                  <InputNumber 
                    v-model="accessory.qty" 
                    :min="0" 
                    :max="999"
                    size="small"
                    class="w-full"
                    @input="markAsChanged"
                  />
                </div>
                                    
                <!-- Unit Price -->
                <div class="field">
                  <label class="block text-xs font-medium text-gray-700 mb-1">Unit Price</label>
                  <InputNumber 
                    v-model="accessory.unitPrice" 
                    :min="0" 
                    :max="999999"
                    mode="currency"
                    currency="GBP"
                    locale="en-GB"
                    size="small"
                    class="w-full"
                    @input="markAsChanged"
                  />
                </div>
                                    
                <!-- Notes -->
                <div class="field">
                  <label class="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                  <InputText 
                    v-model="accessory.notes" 
                    placeholder="Add notes..."
                    size="small"
                    class="w-full"
                    @input="markAsChanged"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
        
    <!-- Footer with Save/Cancel buttons -->
    <div class="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-200">
      <Button 
        label="Cancel" 
        icon="pi pi-times"
        severity="secondary"
        @click="closeModal"
      />
      <Button 
        label="Save Changes" 
        icon="pi pi-save"
        :disabled="!hasChanges"
        :loading="isSubmitting"
        @click="saveChanges"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, defineProps, defineEmits } from 'vue';
import { useToastService } from '@/composables/useToastService';
import { AccessoryService } from '@/services/accessoryService';

// Props
const props = defineProps({
    equipmentListId: {
        type: [Number, String],
        required: true
    },
    parentItemId: {
        type: [Number, String],
        required: true
    },
    localAssetId: {
        type: [Number, String],
        required: true
    }
});

// Emits
const emit = defineEmits(['accessories-added', 'close']);

// Toast service
const toast = useToastService();

// Reactive data
const searchQuery = ref("");
const accessories = ref([]);
const originalAccessories = ref([]);
const isLoadingAccessories = ref(true);
const accessoriesError = ref(null);
const isSubmitting = ref(false);
const hasChanges = ref(false);

// Accessory type options
const accessoryTypeOptions = ref([
    { label: 'Mandatory', value: 'mandatory' },
    { label: 'Optional', value: 'optional' }
]);

// Computed properties
const filteredAccessories = computed(() => {
    if (!accessories.value) return [];
    
    let filtered = accessories.value;
    
    // Filter by search term
    if (searchQuery.value.trim()) {
        const query = searchQuery.value.toLowerCase();
        filtered = filtered.filter(accessory => 
            accessory.name.toLowerCase().includes(query) ||
            accessory.id.toString().includes(query) ||
            (accessory.category && accessory.category.toLowerCase().includes(query))
        );
    }
    
    return filtered;
});

// Methods
const calculateTotal = (accessory) => {
    const qty = parseFloat(accessory.qty || 0);
    const price = parseFloat(accessory.unitPrice || 0);
    return (qty * price).toFixed(2);
};

// Get accessory icon based on category
const getAccessoryIcon = (categoryName) => {
    const category = categoryName?.toLowerCase() || '';
    if (category.includes('cable') || category.includes('wire')) {
        return 'pi pi-link';
    } else if (category.includes('light') || category.includes('lamp')) {
        return 'pi pi-sun';
    } else if (category.includes('audio') || category.includes('sound')) {
        return 'pi pi-volume-up';
    } else if (category.includes('video') || category.includes('camera')) {
        return 'pi pi-video';
    } else if (category.includes('power') || category.includes('electrical')) {
        return 'pi pi-bolt';
    } else {
        return 'pi pi-box';
    }
};

// Get badge severity for accessory type
const getTypeSeverity = (type) => {
    const typeMap = {
        'mandatory': 'danger',
        'optional': 'info',
        'default': 'secondary'
    };
    return typeMap[type] || typeMap.default;
};

const markAsChanged = () => {
    hasChanges.value = true;
};

const deleteAccessory = async (accessory) => {
    const accessoryName = accessory.name || 'this accessory';
    if (confirm(`Are you sure you want to delete "${accessoryName}"?`)) {
        try {
            // Remove from local list immediately
            const index = accessories.value.findIndex(a => a.id === accessory.id);
            if (index > -1) {
                accessories.value.splice(index, 1);
                markAsChanged();
            }
            
            toast.add({
                severity: 'success',
                summary: 'Accessory Deleted',
                detail: `${accessoryName} has been removed`,
                life: 3000
            });
        } catch (error) {
            console.error('Error deleting accessory:', error);
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: `Failed to delete accessory: ${error.message}`,
                life: 5000
            });
        }
    }
};

const saveChanges = async () => {
    if (!hasChanges.value) return;
    
    isSubmitting.value = true;
    
    try {
        // Get list of current accessory IDs
        const currentAccessoryIds = accessories.value.map(a => a.id);
        
        // Find accessories that were deleted (in original but not in current)
        const deletedAccessories = originalAccessories.value.filter(
            original => !currentAccessoryIds.includes(original.id)
        );
        
        // Delete removed accessories
        for (const deletedAccessory of deletedAccessories) {
            await AccessoryService.removeAccessory(props.localAssetId, deletedAccessory.id);
        }
        
        // Update remaining accessories
        const accessoriesToSave = accessories.value.map(accessory => ({
            id: accessory.id,
            accessory_type: accessory.accessoryType,
            quantity: accessory.qty,
            notes: accessory.notes,
            pricing_type: accessory.pricingType,
            // Include original data for unchanged fields
            ...accessory.originalData
        }));
        
        
        // Update each remaining accessory via API
        for (const accessory of accessoriesToSave) {
            await AccessoryService.updateAccessory(props.localAssetId, accessory.id, accessory);
        }
        
        emit('accessories-added', accessoriesToSave);
        
        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Accessories updated successfully',
            life: 3000
        });
        
        hasChanges.value = false;
        closeModal();
        
    } catch (error) {
        console.error('Error saving accessories:', error);
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: `Failed to save accessories: ${error.message}`,
            life: 5000
        });
    } finally {
        isSubmitting.value = false;
    }
};

const closeModal = () => {
    emit('close');
};

// Fetch accessories from API
const fetchAccessories = async () => {
    try {
        isLoadingAccessories.value = true;
        accessoriesError.value = null;
        
        // Fetch accessories for this local asset
        const accessoryRows = await AccessoryService.getAccessories(props.localAssetId);
        
        
        if (Array.isArray(accessoryRows)) {
            // Transform the data to include editable fields
            accessories.value = accessoryRows.map(accessory => ({
                id: accessory.id,
                name: accessory.accessory_local_asset?.display_name || 'Unknown Accessory',
                category: accessory.accessory_local_asset?.master_asset?.asset_type || 'Unknown',
                accessoryType: accessory.accessory_type || 'optional',
                qty: accessory.quantity || 0,
                unitPrice: parseFloat(accessory.accessory_local_asset?.rate || 0),
                notes: accessory.notes || '',
                pricingType: accessory.pricing_type || 'standard',
                originalData: accessory // Keep original data for comparison
            }));
            
            // Store original state for change detection
            originalAccessories.value = JSON.parse(JSON.stringify(accessories.value));
        } else {
            accessories.value = [];
        }
    } catch (error) {
        console.error('Error fetching accessories:', error);
        accessoriesError.value = 'Failed to load accessories. Please try again.';
        accessories.value = [];
    } finally {
        isLoadingAccessories.value = false;
    }
};

// Watch for changes
watch(searchQuery, () => {
    // Search is handled by computed property
});

// Initialize on mount
onMounted(async () => {
    try {
        await fetchAccessories();
    } catch (error) {
        console.error('Error initializing accessory modal:', error);
        accessoriesError.value = 'Failed to load data. Please try again.';
        isLoadingAccessories.value = false;
    }
});
</script>

<style scoped>
/* Custom styles for the modal */
:deep(.p-datatable-tbody > tr > td) {
    padding: 0.5rem;
}

:deep(.p-inputnumber-input) {
    text-align: center;
}

:deep(.p-dropdown) {
    width: 100%;
}
</style>